import React from 'react'
import { useI18n } from '../../../../i18n/I18nContext'
import { workerService } from '../../../../services/worker'
import type { WorkerWorkspaceState } from '../useWorkerWorkspaceData'
import {
  MobileCard,
  MobileChip,
  MobileEmptyState,
  MobileListRow,
  MobileSectionTitle,
  MobileSecondaryButton,
  statusTone,
} from './WorkerMobilePrimitives'
import type { WorkerMobileScreen } from './workerMobileNavigation'

/** "More" — the Android overflow destination, plus the browser-specific settings. */
export const WorkerMobileMore: React.FC<{
  workLogVisible: boolean
  mentorshipVisible: boolean
  pendingSyncCount: number
  /** Other workspaces this user is authorized for, backend-resolved. */
  otherWorkspaces: Array<{ key: string; label: string }>
  onOpen: (screen: WorkerMobileScreen) => void
  onSwitchWorkspace: (workspace: string) => void
  onSignOut: () => void
}> = ({ workLogVisible, mentorshipVisible, pendingSyncCount, otherWorkspaces, onOpen, onSwitchWorkspace, onSignOut }) => {
  const { t } = useI18n()
  return (
    <div className="wm-screen" data-testid="worker-mobile-more">
      <MobileSectionTitle title={t('More')} detail={t('Manage your availability, profile, and app settings.')} />
      <MobileCard as="section">
        <MobileListRow title={t('Availability')} detail={t('Usual week and time off')} onClick={() => onOpen('availability')} testId="worker-mobile-more-availability" />
        {workLogVisible ? <MobileListRow title={t('My Work')} detail={t('Project time and timesheets')} onClick={() => onOpen('worklog')} testId="worker-mobile-more-worklog" /> : null}
        <MobileListRow title={t('Profile')} detail={t('Details, documents, and skills')} onClick={() => onOpen('profile')} testId="worker-mobile-more-profile" />
        <MobileListRow title={t('Decisions & Appeals')} detail={t('Why an action was not available, and how to ask for review')} onClick={() => onOpen('decisions')} testId="worker-mobile-more-decisions" />
        {mentorshipVisible ? <MobileListRow title={t('Mentorship')} detail={t('Your mentors and sessions')} onClick={() => onOpen('mentorship')} testId="worker-mobile-more-mentorship" /> : null}
        <MobileListRow title={t('Learning')} detail={t('Lessons, progress, and your AI coach')} onClick={() => onOpen('learning')} testId="worker-mobile-more-learning" />
        <MobileListRow
          title={t('App permissions')}
          detail={t('Location, notifications, camera, and offline')}
          trailing={pendingSyncCount ? <MobileChip tone="warning">{String(pendingSyncCount)}</MobileChip> : undefined}
          onClick={() => onOpen('permissions')}
          testId="worker-mobile-more-permissions"
        />
      </MobileCard>
      {/*
        Workspace switching for a genuinely multi-workspace user lives here, as a
        secondary control. A worker-only account has nothing to switch to, so
        this section does not render at all and the screen stays purely the
        worker application.
      */}
      {otherWorkspaces.length ? (
        <MobileCard as="section" testId="worker-mobile-workspace-switcher">
          <MobileSectionTitle title={t('Switch workspace')} detail={t('You have access to more than one area.')} />
          {otherWorkspaces.map((workspace) => (
            <MobileListRow
              key={workspace.key}
              title={t(workspace.label)}
              onClick={() => onSwitchWorkspace(workspace.key)}
              testId={`worker-mobile-switch-${workspace.key}`}
            />
          ))}
        </MobileCard>
      ) : null}

      <MobileCard as="section">
        <MobileSecondaryButton label={t('Sign out')} onClick={onSignOut} testId="worker-mobile-more-sign-out" />
      </MobileCard>
    </div>
  )
}

/**
 * Decisions & appeals. Every reason string is authored by the backend — the
 * client never explains a denial in its own words.
 */
export const WorkerMobileDecisions: React.FC<{ state: WorkerWorkspaceState }> = ({ state }) => {
  const { t, formatDateTime } = useI18n()
  const { data, busy, run } = state

  return (
    <div className="wm-screen" data-testid="worker-mobile-decisions">
      <MobileSectionTitle
        title={t('Decisions & Appeals')}
        detail={t('These explain why something was not available and whether you can ask for a human review.')}
      />
      {data.decisions.length ? data.decisions.map((decision) => {
        const appeal = data.appeals.find((row) => row.decision_id === decision.id)
        return (
          <MobileCard key={decision.id} testId="worker-mobile-decision">
            <div className="wm-card__heading">
              <div>
                <h3>{decision.summary}</h3>
                <small>{formatDateTime(decision.created_at, { dateStyle: 'medium', timeStyle: 'short' })}</small>
              </div>
              <MobileChip tone={statusTone(decision.outcome)}>{t(String(decision.outcome).replace(/_/g, ' '))}</MobileChip>
            </div>
            {decision.reasons?.length ? (
              <ul className="wm-list">{decision.reasons.map((reason, index) => <li key={`${reason.code}-${index}`}>{reason.message}</li>)}</ul>
            ) : null}
            {appeal ? (
              <p className="wm-guidance">{t('Review requested — status: {status}', { status: t(String(appeal.status).replace(/_/g, ' ')) })}</p>
            ) : decision.appealable ? (
              <MobileSecondaryButton
                label={t('Request human review')}
                disabled={Boolean(busy)}
                testId="worker-mobile-request-review"
                onClick={() => {
                  const message = window.prompt(t('Tell the reviewer what you would like them to look at'))?.trim()
                  if (message) {
                    void run(`appeal-${decision.id}`, () => workerService.submitDecisionAppeal(decision.id, message), t('Review requested.'))
                  }
                }}
              />
            ) : null}
          </MobileCard>
        )
      }) : (
        <MobileEmptyState title={t('No recorded decisions')} detail={t('Decisions about your access to work appear here with their reasons.')} />
      )}
    </div>
  )
}
